import React, { useCallback, useMemo } from 'react'
import { Container } from '../../../components'
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { useSchedulerAnalyticsStyles, useSchedulerHeaderStyles } from './styles'
import {
    AreaStatusConfig,
    AreaStatusTag,
    BaseWorkorder,
    findOccupiableAreasWithoutWorkorders,
    Schedule,
    SEE_INCOME_FINANCES,
    SEE_VENDOR_FINANCES,
    WorkorderAnalyticDict,
    WorkorderStatus,
} from '../../../models'
import { getStatusComparisonValue } from '../../../models/WorkOrder/services'
import { InfrastructureFilterOptions } from '../../../store'
import { hasPermission } from '../../../models/Users/services'
import { useUser } from '../../../hooks'
import { currencyFormatter } from '../../../helpers'
import { ManualUnitReadyMark } from '../unitReady'

const WORKORDER_TAB = 0
const FINANCE_TAB = 1
const AREA_STATUS_TAB = 2

const ALL_WORKORDER_STATUSES = Object.values(WorkorderStatus)

type StatTile = {
    label: string
    value: React.ReactNode
    tooltip?: React.ReactNode
    onClick?: () => void
}

type FinanceRow = {
    label: string
    value: React.ReactNode
    disabled?: boolean
}

interface Props {
    analyticDict: WorkorderAnalyticDict
    scheduleDetail?: Schedule
    areaStatusTagList?: AreaStatusTag[]
    workorderList?: BaseWorkorder[]
    setInfrastructureFilter: (
        key: keyof InfrastructureFilterOptions,
        value: unknown,
    ) => void
    fullWorkorderList?: BaseWorkorder[]
    viewAnalyticsTab: number
    visibleUnitIdSet?: Set<number>
    manualUnitReady?: Map<number, ManualUnitReadyMark>
}
export const SchedulerAnalyticsHeadline = (props: Props) => {
    const {
        analyticDict,
        scheduleDetail,
        areaStatusTagList,
        setInfrastructureFilter,
        fullWorkorderList,
        viewAnalyticsTab,
        visibleUnitIdSet,
        manualUnitReady,
    } = props
    const classes = useSchedulerAnalyticsStyles()
    const theme = useTheme()
    const { workspaceUser } = useUser()
    const incomePermission = hasPermission(workspaceUser, SEE_INCOME_FINANCES)
    const costPermission = hasPermission(workspaceUser, SEE_VENDOR_FINANCES)

    const filteredAreaStatusTagList = useMemo(() => {
        if (!areaStatusTagList) {
            return []
        }
        if (!visibleUnitIdSet) {
            return areaStatusTagList
        }
        return areaStatusTagList.filter(
            (ast) => ast.unit?.id != null && visibleUnitIdSet.has(ast.unit.id),
        )
    }, [areaStatusTagList, visibleUnitIdSet])

    const astCountMap = useMemo(() => {
        const countMap: Record<string, number> = {}
        filteredAreaStatusTagList.forEach((ast) => {
            const areaConfig = ast.area.area_config
            if (areaConfig.occupiable) {
                const astName = ast.area_status_config.name
                countMap[astName] = (countMap[astName] ?? 0) + 1
            }
        })
        return countMap
    }, [filteredAreaStatusTagList])

    const areaStatusConfigByName = useMemo(() => {
        const configMap: Record<string, AreaStatusConfig> = {}
        filteredAreaStatusTagList.forEach((ast) => {
            const config = ast.area_status_config
            if (config && configMap[config.name] === undefined) {
                configMap[config.name] = config
            }
        })
        return configMap
    }, [filteredAreaStatusTagList])

    const occAreasWithoutWorkorders = useMemo(
        () =>
            findOccupiableAreasWithoutWorkorders(
                filteredAreaStatusTagList,
                fullWorkorderList ?? [],
            ),
        [filteredAreaStatusTagList, fullWorkorderList],
    )
    const unitsReadyCount = useMemo(() => {
        const readyIds = new Set<number>()
        const minComplete = getStatusComparisonValue(WorkorderStatus.COMPLETE)
        if (fullWorkorderList) {
            const unitWorkorders = new Map<number, boolean>()
            for (const wo of fullWorkorderList) {
                if (wo.unit_id == null) continue
                if (visibleUnitIdSet && !visibleUnitIdSet.has(wo.unit_id)) {
                    continue
                }
                const prevReady = unitWorkorders.get(wo.unit_id)
                if (prevReady === false) continue
                unitWorkorders.set(
                    wo.unit_id,
                    getStatusComparisonValue(wo.status) >= minComplete,
                )
            }
            unitWorkorders.forEach((ready, unitId) => {
                if (ready) readyIds.add(unitId)
            })
        }
        manualUnitReady?.forEach((mark, unitId) => {
            if (visibleUnitIdSet && !visibleUnitIdSet.has(unitId)) return
            if (mark.status === 'READY') {
                readyIds.add(unitId)
            } else {
                readyIds.delete(unitId)
            }
        })
        return readyIds.size
    }, [fullWorkorderList, manualUnitReady, visibleUnitIdSet])

    const renderAreaStatusValue = useCallback(
        (status: string) => {
            const count = astCountMap[status] ?? 0
            const unassignedCount = occAreasWithoutWorkorders[status] ?? 0

            if (unassignedCount > 0) {
                return (
                    <>
                        {count}{' '}
                        <Tooltip
                            title={`${unassignedCount} areas without workorders`}
                        >
                            <span
                                style={{
                                    color: theme.palette.error.main,
                                    cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setInfrastructureFilter(
                                        'unassignedOccAreas',
                                        status,
                                    )
                                }}
                            >
                                ({unassignedCount})
                            </span>
                        </Tooltip>
                    </>
                )
            }

            return count
        },
        [
            astCountMap,
            occAreasWithoutWorkorders,
            setInfrastructureFilter,
            theme,
        ],
    )

    const applyStatusFilter = useCallback(
        (statuses: WorkorderStatus[]) => {
            const newStatusFilter = {} as { [key in WorkorderStatus]: boolean }
            ALL_WORKORDER_STATUSES.forEach((status) => {
                newStatusFilter[status] = statuses.includes(status)
            })
            setInfrastructureFilter('behindWorkorders', false)
            setInfrastructureFilter('statusFilter', newStatusFilter)
        },
        [setInfrastructureFilter],
    )

    const applyBehindFilter = useCallback(() => {
        applyStatusFilter([])
        setInfrastructureFilter('behindWorkorders', true)
    }, [applyStatusFilter, setInfrastructureFilter])

    const applyAreaStatusFilter = useCallback(
        (config: AreaStatusConfig) => {
            setInfrastructureFilter('areaStatusFilter', {
                [config.id]: {
                    inFilter: true,
                    areaStatusConfig: config,
                },
                filterLength: 1,
            })
        },
        [setInfrastructureFilter],
    )

    const workorderTiles: StatTile[] = useMemo(
        () => [
            {
                label: 'Total Work Orders',
                value: analyticDict.workorderCount,
                onClick: () => applyStatusFilter([]),
            },
            {
                label: 'In Progress',
                value:
                    analyticDict.IN_PROGRESS +
                    analyticDict.PAUSE +
                    analyticDict.GO_BACK,
                onClick: () =>
                    applyStatusFilter([
                        WorkorderStatus.IN_PROGRESS,
                        WorkorderStatus.PAUSE,
                        WorkorderStatus.GO_BACK,
                    ]),
            },
            {
                label: 'Not Started',
                value: analyticDict.notStarted,
                onClick: () => applyStatusFilter([WorkorderStatus.ASSIGNED]),
            },
            {
                label: 'Behind Count',
                value: analyticDict.behindTrack,
                onClick: applyBehindFilter,
            },
            {
                label: 'Complete',
                value: analyticDict.COMPLETE,
                onClick: () => applyStatusFilter([WorkorderStatus.COMPLETE]),
            },
            {
                label: 'Approved',
                value: analyticDict.APPROVED,
                onClick: () => applyStatusFilter([WorkorderStatus.APPROVED]),
            },
            {
                label: 'Invoiced',
                value: analyticDict.INVOICED,
                onClick: () => applyStatusFilter([WorkorderStatus.INVOICED]),
            },
            {
                label: 'Invoice Request',
                value: analyticDict.INVOICE_REQUEST,
                onClick: () =>
                    applyStatusFilter([WorkorderStatus.INVOICE_REQUEST]),
            },
            {
                label: 'Invoiced Out',
                value: analyticDict.INVOICED_OUT,
                onClick: () =>
                    applyStatusFilter([WorkorderStatus.INVOICED_OUT]),
            },
            {
                label: 'Units Ready',
                value: unitsReadyCount,
                onClick: () =>
                    setInfrastructureFilter('readyUnitFilter', {
                        enabled: true,
                        value: WorkorderStatus.COMPLETE,
                    }),
            },
        ],
        [
            analyticDict,
            applyStatusFilter,
            applyBehindFilter,
            unitsReadyCount,
            setInfrastructureFilter,
        ],
    )

    const financeTiles: StatTile[] = useMemo(() => {
        const tiles: StatTile[] = []

        if (costPermission) {
            tiles.push(
                {
                    label: 'Set Budget',
                    value: currencyFormatter.format(
                        scheduleDetail?.baseline_budget ?? 0,
                    ),
                },
                {
                    label: 'Forecasted Budget',
                    value: currencyFormatter.format(
                        analyticDict.forecastedBudget,
                    ),
                },
                {
                    label: 'Invoiced Budget',
                    value: currencyFormatter.format(
                        analyticDict.invoicedBudget,
                    ),
                },
                {
                    label: 'Invoiced Out Budget',
                    value: currencyFormatter.format(
                        analyticDict.invoicedOutBudget,
                    ),
                },
            )
        }

        if (incomePermission) {
            tiles.push({
                label: 'Forecasted Income',
                value: currencyFormatter.format(
                    analyticDict.incomeForecast ?? 0,
                ),
            })
        }

        if (incomePermission && costPermission) {
            tiles.push({
                label: 'Forecasted Profit',
                value: currencyFormatter.format(
                    analyticDict.incomeForecast - analyticDict.forecastedBudget,
                ),
            })
        }

        if (incomePermission) {
            tiles.push({
                label: 'Total Invoiced Budget',
                value: currencyFormatter.format(
                    analyticDict.invoicedBudget +
                        analyticDict.invoicedOutBudget,
                ),
            })
        }

        tiles.push({
            label: 'Pending Change Orders',
            value: analyticDict.changeOrderAnalytics.pendingCount,
        })

        if (costPermission) {
            tiles.push({
                label: 'Pending Change Order Cost',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.pendingCost,
                ),
            })
        }

        if (incomePermission) {
            tiles.push({
                label: 'Pending Change Order Income',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.pendingIncome,
                ),
            })
        }

        tiles.push({
            label: 'Approved Change Orders',
            value: analyticDict.changeOrderAnalytics.approvedCount,
        })

        if (costPermission) {
            tiles.push({
                label: 'Approved Change Order Cost',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.approvedCost,
                ),
            })
        }

        if (incomePermission) {
            tiles.push({
                label: 'Approved Change Order Income',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.approvedIncome,
                ),
            })
        }

        return tiles
    }, [analyticDict, costPermission, incomePermission, scheduleDetail])

    const incomeRows: FinanceRow[] = useMemo(() => {
        const rows: FinanceRow[] = [
            {
                label: 'Forecasted Income',
                value: currencyFormatter.format(
                    analyticDict.incomeForecast ?? 0,
                ),
                disabled: !incomePermission,
            },
            {
                label: 'Forecasted Profit',
                value: currencyFormatter.format(
                    analyticDict.incomeForecast - analyticDict.forecastedBudget,
                ),
                disabled: !(incomePermission && costPermission),
            },
            {
                label: 'Total Invoiced Budget',
                value: currencyFormatter.format(
                    analyticDict.invoicedBudget +
                        analyticDict.invoicedOutBudget,
                ),
                disabled: !incomePermission,
            },
        ]

        return rows
    }, [analyticDict, incomePermission, costPermission])

    const costRows: FinanceRow[] = useMemo(() => {
        const rows: FinanceRow[] = [
            {
                label: 'Set Budget',
                value: currencyFormatter.format(
                    scheduleDetail?.baseline_budget ?? 0,
                ),
                disabled: !costPermission,
            },
            {
                label: 'Forecasted Budget',
                value: currencyFormatter.format(analyticDict.forecastedBudget),
                disabled: !costPermission,
            },
            {
                label: 'Invoiced Budget',
                value: currencyFormatter.format(analyticDict.invoicedBudget),
                disabled: !costPermission,
            },
            {
                label: 'Invoiced Out Budget',
                value: currencyFormatter.format(analyticDict.invoicedOutBudget),
                disabled: !costPermission,
            },
        ]

        return rows
    }, [analyticDict, costPermission, scheduleDetail])

    const changeOrderRows: FinanceRow[] = useMemo(() => {
        const rows: FinanceRow[] = [
            {
                label: 'Pending Change Orders',
                value: analyticDict.changeOrderAnalytics.pendingCount,
            },
            {
                label: 'Pending Change Order Cost',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.pendingCost,
                ),
                disabled: !costPermission,
            },
            {
                label: 'Pending Change Order Income',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.pendingIncome,
                ),
                disabled: !incomePermission,
            },
            {
                label: 'Approved Change Orders',
                value: analyticDict.changeOrderAnalytics.approvedCount,
            },
            {
                label: 'Approved Change Order Cost',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.approvedCost,
                ),
                disabled: !costPermission,
            },
            {
                label: 'Approved Change Order Income',
                value: currencyFormatter.format(
                    analyticDict.changeOrderAnalytics.approvedIncome,
                ),
                disabled: !incomePermission,
            },
        ]

        return rows
    }, [analyticDict, costPermission, incomePermission])

    const areaStatusTiles: StatTile[] = useMemo(() => {
        const makeTile = (label: string, statusKey: string): StatTile => {
            const config = areaStatusConfigByName[statusKey]
            return {
                label,
                value: renderAreaStatusValue(statusKey),
                onClick: config
                    ? () => applyAreaStatusFilter(config)
                    : undefined,
            }
        }

        return [
            makeTile('Vacant', 'V'),
            makeTile('Renewal', 'R'),
            makeTile('Transfer In', 'TI'),
            makeTile('Transfer Out', 'TO'),
            makeTile('Double Transfer', 'DT'),
            makeTile('Early Move In', 'EM'),
            makeTile('Do Not Turn', 'DNT'),
            makeTile('Occupied', 'O'),
            makeTile('Yes', 'Yes'),
            makeTile('No', 'No'),
        ]
    }, [renderAreaStatusValue, areaStatusConfigByName, applyAreaStatusFilter])

    const activeTiles = useMemo(() => {
        if (viewAnalyticsTab === FINANCE_TAB) {
            return financeTiles
        }

        if (viewAnalyticsTab === AREA_STATUS_TAB) {
            return areaStatusTiles
        }

        return workorderTiles
    }, [viewAnalyticsTab, workorderTiles, financeTiles, areaStatusTiles])

    const tileClassName = useMemo(() => {
        if (viewAnalyticsTab === AREA_STATUS_TAB) {
            return `${classes.analyticsStatTile} ${classes.analyticsStatTileAreaStatus}`
        }

        if (viewAnalyticsTab === WORKORDER_TAB) {
            return `${classes.analyticsStatTile} ${classes.analyticsStatTileWorkorder}`
        }

        return classes.analyticsStatTile
    }, [classes, viewAnalyticsTab])

    return (
        <Container className={classes.root}>
            {viewAnalyticsTab === FINANCE_TAB ? (
                <Container className={classes.financeTables}>
                    {/* Change Orders Table */}
                    <Box className={classes.financeTable}>
                        <Typography className={classes.financeTableTitle}>
                            Change Orders
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {changeOrderRows.map((row) => {
                                    const cellContent = row.disabled
                                        ? '-'
                                        : row.value
                                    const rowClasses = row.disabled
                                        ? `${classes.financeTableRowMuted} ${classes.financeTableRowTooltip}`
                                        : undefined
                                    const rowMarkup = (
                                        <TableRow key={row.label}>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {row.label}
                                            </TableCell>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableValueCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {cellContent}
                                            </TableCell>
                                        </TableRow>
                                    )

                                    return row.disabled ? (
                                        <Tooltip
                                            key={row.label}
                                            title="Missing Permission to see this"
                                        >
                                            {rowMarkup}
                                        </Tooltip>
                                    ) : (
                                        rowMarkup
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Cost Table */}
                    <Box className={classes.financeTable}>
                        <Typography className={classes.financeTableTitle}>
                            Cost
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {costRows.map((row) => {
                                    const cellContent = row.disabled
                                        ? '-'
                                        : row.value
                                    const rowClasses = row.disabled
                                        ? `${classes.financeTableRowMuted} ${classes.financeTableRowTooltip}`
                                        : undefined
                                    const rowMarkup = (
                                        <TableRow key={row.label}>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {row.label}
                                            </TableCell>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableValueCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {cellContent}
                                            </TableCell>
                                        </TableRow>
                                    )

                                    return row.disabled ? (
                                        <Tooltip
                                            key={row.label}
                                            title="Missing Permission to see this"
                                        >
                                            {rowMarkup}
                                        </Tooltip>
                                    ) : (
                                        rowMarkup
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Income Table */}
                    <Box className={classes.financeTable}>
                        <Typography className={classes.financeTableTitle}>
                            Income
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {incomeRows.map((row) => {
                                    const cellContent = row.disabled
                                        ? '-'
                                        : row.value
                                    const rowClasses = row.disabled
                                        ? `${classes.financeTableRowMuted} ${classes.financeTableRowTooltip}`
                                        : undefined
                                    const rowMarkup = (
                                        <TableRow key={row.label}>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {row.label}
                                            </TableCell>
                                            <TableCell
                                                className={`${
                                                    classes.financeTableValueCell
                                                } ${rowClasses ?? ''}`}
                                            >
                                                {cellContent}
                                            </TableCell>
                                        </TableRow>
                                    )

                                    return row.disabled ? (
                                        <Tooltip
                                            key={row.label}
                                            title="Missing Permission to see this"
                                        >
                                            {rowMarkup}
                                        </Tooltip>
                                    ) : (
                                        rowMarkup
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                </Container>
            ) : (
                <Container className={classes.analyticsStatTiles}>
                    {activeTiles.map((tile) => (
                        <Box
                            key={tile.label}
                            className={tileClassName}
                            onClick={tile.onClick}
                            style={
                                tile.onClick ? { cursor: 'pointer' } : undefined
                            }
                        >
                            <Box className={classes.analyticsStatContent}>
                                <Typography
                                    component="span"
                                    className={classes.analyticsStatLabel}
                                >
                                    {tile.label}
                                </Typography>
                                <Typography
                                    component="span"
                                    className={classes.analyticsStatValue}
                                    style={
                                        tile.label === 'Behind Count' &&
                                        Number(tile.value) > 0
                                            ? {
                                                  color:
                                                      theme.palette.error.main,
                                              }
                                            : undefined
                                    }
                                >
                                    {tile.value}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Container>
            )}
        </Container>
    )
}
